import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { syncAppBadgeToUnread } from '@/utils/appBadge';
import { fetchProfilesByIds } from '@/utils/fetchProfilesByIds';
import type { ChatMessage, ChatTarget, MessageRead } from '../types';
import {
  buildSenderNameByUserId,
  resolveReaderDisplayName,
} from '../utils/resolveReaderDisplayName';
import { chatKey, filterColumn, targetId } from '../chatTarget';

/** Receipts arrive over realtime and carry message_id + user_id, so they are applied
 *  directly. Only unknown reader NAMES need a lookup, and those are batched. */
const NAME_LOOKUP_DEBOUNCE_MS = 250;

type RawReadRow = {
  message_id: string;
  user_id: string;
  read_at: string;
  event_id?: string | null;
  direct_chat_id?: string | null;
  department_chat_id?: string | null;
};

export function useMessageReads(
  target: ChatTarget,
  messages: ChatMessage[],
  currentUserId: string | null
) {
  const key = chatKey(target);
  const col = filterColumn(target);
  const id = targetId(target);

  const [readsByMessageId, setReadsByMessageId] = useState<Record<string, MessageRead[]>>({});

  const messagesRef = useRef<ChatMessage[]>([]);
  const currentUserIdRef = useRef<string | null>(null);
  const nameCacheRef = useRef<Record<string, string>>({});
  const pendingNameIdsRef = useRef<Set<string>>(new Set());
  const nameFetchTimerRef = useRef<number | null>(null);

  useEffect(() => {
    messagesRef.current = messages;
    refreshReadNamesFromMessages();
  }, [messages]);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    setReadsByMessageId({});
  }, [key, currentUserId]);

  const markAllAsRead = async () => {
    if (!id) return;
    const uid = currentUserIdRef.current;
    if (!uid) return;

    // One RPC per kind, and they take differently-named arguments -- calling the wrong
    // one silently marks nothing, so this is an exhaustive switch rather than a ternary.
    const { error } = await (() => {
      switch (target.kind) {
        case 'event':
          return supabase.rpc('mark_event_messages_read', { p_event_id: id, p_user_id: uid });
        case 'direct':
          return supabase.rpc('mark_direct_chat_messages_read', {
            p_direct_chat_id: id,
            p_user_id: uid,
          });
        case 'department':
          return supabase.rpc('mark_department_chat_messages_read', {
            p_department_chat_id: id,
            p_user_id: uid,
          });
      }
    })();
    if (error) {
      console.error('[EventChat] Failed to bulk mark messages as read', error);
    }

    // The chat's notification row points at messages that are now on screen. Leaving it
    // unread would keep the inbox -- and the app-icon badge, which counts unread rows --
    // claiming messages the user is looking at. The RPC reports how many rows it cleared
    // so the badge resync only runs when something actually changed; this is called on
    // every new message and every return to visibility.
    const { data: cleared, error: notificationError } = await supabase.rpc(
      'mark_chat_notifications_read',
      { p_chat_key: key }
    );
    if (notificationError) {
      console.error('[EventChat] Failed to mark chat notifications as read', notificationError);
      return;
    }
    if (typeof cleared === 'number' && cleared > 0) {
      void syncAppBadgeToUnread(uid);
    }
  };

  async function refetchReads(messageIds: string[], checkMounted?: () => boolean) {
    if (messageIds.length === 0) return;
    const { data: readsData, error: readsError } = await supabase
      .from('message_reads')
      .select('message_id, user_id, read_at')
      .in('message_id', messageIds);
    if (readsError) {
      console.error('[EventChat] message_reads select failed', readsError);
      return;
    }
    if (checkMounted && !checkMounted()) return;
    const reads = (readsData as { message_id: string; user_id: string; read_at: string }[] | null) ?? [];
    const userIds = [...new Set(reads.map((r) => r.user_id))];
    const { profiles: readerProfiles } = await fetchProfilesByIds<{
      id: string;
      first_name: string | null;
      last_name: string | null;
    }>(userIds, 'id, first_name, last_name', 'useMessageReads');
    const profilesMap: Record<string, { first_name: string | null; last_name: string | null }> =
      Object.fromEntries(
        readerProfiles.map((p) => [p.id, { first_name: p.first_name, last_name: p.last_name }]),
      );
    if (checkMounted && !checkMounted()) return;
    const senderNameByUserId = buildSenderNameByUserId(messagesRef.current);
    for (const [id, prof] of Object.entries(profilesMap)) {
      const name = [prof.first_name, prof.last_name].filter(Boolean).join(' ').trim();
      if (name) nameCacheRef.current[id] = name;
    }
    const byMessage: Record<string, MessageRead[]> = {};
    for (const r of reads) {
      const full_name = resolveReaderDisplayName(r.user_id, profilesMap, senderNameByUserId);
      const rec: MessageRead = {
        message_id: r.message_id,
        user_id: r.user_id,
        read_at: r.read_at,
        user: { id: r.user_id, full_name },
      };
      if (!byMessage[r.message_id]) byMessage[r.message_id] = [];
      byMessage[r.message_id].push(rec);
    }
    setReadsByMessageId((prev) => {
      const next = { ...prev };
      for (const [messageId, newReads] of Object.entries(byMessage)) {
        const merged = [...(next[messageId] ?? [])];
        for (const r of newReads) {
          const idx = merged.findIndex((x) => x.user_id === r.user_id);
          if (idx >= 0) {
            merged[idx] = r;
          } else {
            merged.push(r);
          }
        }
        next[messageId] = merged;
      }
      return next;
    });
  }

  /**
   * Apply a realtime receipt straight from its payload.
   *
   * This replaces a per-receipt `refetchReads` round trip. With N clients in a chat,
   * every receipt was broadcast to all N, and each re-queried, so one message cost
   * roughly N^2 queries (measured: 5 messages x 10 clients = 500 refetches).
   */
  function applyReadRow(row: RawReadRow) {
    const senderNameByUserId = buildSenderNameByUserId(messagesRef.current);
    const known = nameCacheRef.current[row.user_id] ?? senderNameByUserId[row.user_id]?.trim();
    const full_name = known || 'Unknown';

    setReadsByMessageId((prev) => {
      const existing = prev[row.message_id] ?? [];
      const idx = existing.findIndex((r) => r.user_id === row.user_id);
      const rec: MessageRead = {
        message_id: row.message_id,
        user_id: row.user_id,
        read_at: row.read_at,
        user: { id: row.user_id, full_name },
      };
      if (idx >= 0) {
        const cur = existing[idx];
        if (cur.read_at === row.read_at && cur.user?.full_name === full_name) return prev;
        const merged = [...existing];
        merged[idx] = rec;
        return { ...prev, [row.message_id]: merged };
      }
      return { ...prev, [row.message_id]: [...existing, rec] };
    });

    if (!known) queueNameLookup(row.user_id);
  }

  /** Batch name lookups for readers we have never seen; usually a handful per chat. */
  function queueNameLookup(userId: string) {
    if (nameCacheRef.current[userId]) return;
    pendingNameIdsRef.current.add(userId);
    if (nameFetchTimerRef.current !== null) return;
    nameFetchTimerRef.current = window.setTimeout(() => {
      nameFetchTimerRef.current = null;
      void flushNameLookups();
    }, NAME_LOOKUP_DEBOUNCE_MS);
  }

  async function flushNameLookups() {
    const ids = [...pendingNameIdsRef.current].filter((id) => !nameCacheRef.current[id]);
    pendingNameIdsRef.current.clear();
    if (ids.length === 0) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .in('id', ids);
    if (error) {
      console.error('[EventChat] reader name lookup failed', error);
      return;
    }
    let learned = false;
    for (const p of (data ?? []) as { id: string; first_name: string | null; last_name: string | null }[]) {
      const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
      if (name && !nameCacheRef.current[p.id]) {
        nameCacheRef.current[p.id] = name;
        learned = true;
      }
    }
    if (learned) applyNameCacheToState();
  }

  /** Upgrade any placeholder names once their profile has been resolved. */
  function applyNameCacheToState() {
    setReadsByMessageId((prev) => {
      let changed = false;
      const next: Record<string, MessageRead[]> = {};
      for (const [messageId, reads] of Object.entries(prev)) {
        next[messageId] = reads.map((read) => {
          const cached = nameCacheRef.current[read.user_id];
          if (!cached || read.user?.full_name === cached) return read;
          changed = true;
          return { ...read, user: { id: read.user_id, full_name: cached } };
        });
      }
      return changed ? next : prev;
    });
  }

  function refreshReadNamesFromMessages() {
    setReadsByMessageId((prev) => {
      const senderNameByUserId = buildSenderNameByUserId(messagesRef.current);
      let changed = false;
      const next: Record<string, MessageRead[]> = {};

      for (const [messageId, reads] of Object.entries(prev)) {
        const updated = reads.map((read) => {
          if (read.user?.full_name && read.user.full_name !== 'Unknown') return read;
          const fallbackName = senderNameByUserId[read.user_id]?.trim();
          if (!fallbackName) return read;
          changed = true;
          return {
            ...read,
            user: { id: read.user_id, full_name: fallbackName },
          };
        });
        next[messageId] = updated;
      }

      return changed ? next : prev;
    });
  }

  useEffect(() => {
    let isMounted = true;

    const refreshReadsIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void markAllAsRead();
      const ids = messagesRef.current.map((m) => m.id);
      if (ids.length > 0) void refetchReads(ids, () => isMounted);
    };

    refreshReadsIfVisible();

    document.addEventListener('visibilitychange', refreshReadsIfVisible);

    let readsSubscribedOnce = false;
    const readsChannel = supabase
      .channel(`message_reads:${key}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_reads',
          filter: `${col}=eq.${id}`,
        },
        (payload) => {
          applyReadRow(payload.new as RawReadRow);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'message_reads',
          filter: `${col}=eq.${id}`,
        },
        (payload) => {
          applyReadRow(payload.new as RawReadRow);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED' && readsSubscribedOnce && isMounted) {
          const ids = messagesRef.current.map((m) => m.id);
          if (ids.length > 0) refetchReads(ids, () => isMounted);
        }
        if (status === 'SUBSCRIBED') readsSubscribedOnce = true;
      });

    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', refreshReadsIfVisible);
      supabase.removeChannel(readsChannel);
    };
  }, [col, id, key]); // eslint-disable-line react-hooks/exhaustive-deps -- markAllAsRead/refetchReads + messagesRef; keep the thread keys only so Realtime channel is not torn down every render

  return { readsByMessageId, markAllAsRead, refetchReads };
}

